import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Package, Building2, Loader2 } from 'lucide-react';
import api from '../lib/api';
import RetailerLayout from '../components/RetailerLayout';
import PartnerBadges from '../components/PartnerBadges';
import ProductCatalogView from '../components/catalog/ProductCatalogView';

/**
 * PartnerCatalogPage V2 — Catalogue distributeur avec filtres (Phase 3.4.2)
 * Route : /partner/:slug/products
 */
const PartnerCatalogPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  
  const [partner, setPartner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPartner = async () => {
      if (!slug) { setError('not_found'); setLoading(false); return; }
      try {
        setLoading(true);
        setError(null);
        let response = await api.get(`/partners?slug=${slug}&is_active=true`);
        let partners = response.data || [];
        if (partners.length === 0) {
          try {
            response = await api.get(`/partners/${slug}`);
            if (response.data) partners = [response.data];
          } catch { /* ID not found */ }
        }
        if (partners.length === 0) { setError('not_found'); setLoading(false); return; }
        const partnerData = partners[0];
        setPartner(partnerData);
        document.title = `Catalogue ${partnerData.nom || 'Partenaire'} - Games Visor`;
        setLoading(false);
      } catch {
        setError('api_error');
        setLoading(false);
      }
    };
    fetchPartner();
  }, [slug]);

  useEffect(() => { return () => { document.title = 'Games Visor'; }; }, []);

  const contextFilters = useMemo(() => {
    if (!partner) return {};
    return { partner_id: partner.id };
  }, [partner]);

  if (loading) {
    return (
      <RetailerLayout>
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="h-12 w-12 animate-spin text-gray-400" data-testid="catalog-loading" />
        </div>
      </RetailerLayout>
    );
  }

  if (error === 'not_found') {
    return (
      <RetailerLayout>
        <div className="min-h-screen flex items-center justify-center px-4">
          <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
            <Package className="h-16 w-16 mx-auto text-gray-400 mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Catalogue non trouvé</h1>
            <p className="text-gray-600 mb-6">
              Le partenaire <span className="font-mono bg-gray-100 px-2 py-1 rounded">{slug}</span> n'existe pas.
            </p>
            <button onClick={() => navigate('/')} className="bg-gray-800 hover:bg-gray-900 text-white font-semibold px-6 py-3 rounded-lg transition-colors" data-testid="back-home-btn">
              Retour à l'accueil
            </button>
          </div>
        </div>
      </RetailerLayout>
    );
  }

  if (error === 'api_error') {
    return (
      <RetailerLayout>
        <div className="min-h-screen flex items-center justify-center px-4">
          <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Erreur de chargement</h1>
            <p className="text-gray-600 mb-6">Impossible de charger le catalogue. Veuillez réessayer.</p>
            <button onClick={() => window.location.reload()} className="bg-gray-800 hover:bg-gray-900 text-white font-semibold px-6 py-3 rounded-lg transition-colors">
              Réessayer
            </button>
          </div>
        </div>
      </RetailerLayout>
    );
  }

  if (!partner) return null;

  const displayName = partner.nom || partner.name || 'Partenaire';

  const partnerHeader = (
    <div className="mb-4">
      <Link
        to={`/partner/${slug}`}
        className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors mb-3 text-sm"
        data-testid="back-to-partner-btn"
      >
        <ArrowLeft className="h-4 w-4" />
        Retour à la page {displayName}
      </Link>
      <div className="flex items-center gap-4">
        {partner.logo && (
          <div className="flex-shrink-0 flex items-center justify-center rounded-lg border bg-white overflow-hidden" style={{ width: '56px', height: '56px', padding: '6px' }}>
            <img src={partner.logo} alt={`Logo ${displayName}`} className="max-w-full max-h-full object-contain" />
          </div>
        )}
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Building2 className="h-5 w-5 text-gray-600" />
            Catalogue {displayName}
          </h1>
          <PartnerBadges
            supplierType={partner.supplier_type || 'other'}
            capabilities={partner.capabilities}
            isPartner={partner.is_partner === true}
            size="sm"
            className="mt-1"
          />
        </div>
      </div>
    </div>
  );

  return (
    <RetailerLayout>
      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '24px 16px' }} data-testid="partner-catalog-page">
        <ProductCatalogView
          contextFilters={contextFilters}
          header={partnerHeader}
          emptyMessage={`Aucun produit disponible chez ${displayName}.`}
        />
      </div>
    </RetailerLayout>
  );
};

export default PartnerCatalogPage;
